import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import {
  AvailabilityHead,
  PeriodStrip,
  ResponseChart,
  StatusTiles,
} from '../components/Availability.js';
import { LocationsCard } from '../components/Locations.js';
import { LoadError, Loading, Notice, Segmented } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate, formatDateTime, formatDuration } from '../i18n.js';
import { useMe } from '../session.js';

type Filter = 'all' | 'open' | 'resolved';
type Incident = Schemas['Incident'];
type DayState = 'ok' | 'degraded' | 'down' | 'none';
interface Uptime {
  range: string;
  uptimePct: number | null;
  series: { t: string; checks: number; failed: number }[];
}

const DAY_MS = 86_400_000;

/** Days are UTC calendar days, the same buckets the uptime series uses. */
const dayKey = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const endOf = (i: Incident, now: number) => (i.resolvedAt ? Date.parse(i.resolvedAt) : now);

/** 90 days of uptime; plans with less history fall back to the 7 days every plan shows. */
async function fetchUptime(siteId: string): Promise<Uptime> {
  const get = (range: '90d' | '7d') =>
    unwrap(api.GET('/sites/{siteId}/uptime', { params: { path: { siteId }, query: { range } } }));
  try {
    return await get('90d');
  } catch (e) {
    if (e instanceof ApiError && e.code === 'PLAN_LIMIT_REACHED') return get('7d');
    throw e;
  }
}

/** One state per day for the last `days` days, oldest first. */
export function calendarDays(
  days: number,
  series: Uptime['series'],
  incidents: Incident[],
  now: number,
): { day: string; state: DayState }[] {
  const checks = new Map<string, { checks: number; failed: number }>();
  for (const b of series) {
    const k = dayKey(Date.parse(b.t));
    const c = checks.get(k) ?? { checks: 0, failed: 0 };
    checks.set(k, { checks: c.checks + b.checks, failed: c.failed + b.failed });
  }
  const today = Date.parse(`${dayKey(now)}T00:00:00Z`);
  return Array.from({ length: days }, (_, n) => {
    const start = today - (days - 1 - n) * DAY_MS;
    const day = dayKey(start);
    const hits = incidents.filter(
      (i) => Date.parse(i.startedAt) < start + DAY_MS && endOf(i, now) >= start,
    );
    const c = checks.get(day);
    const state: DayState = hits.some((i) => i.kind === 'downtime')
      ? 'down'
      : hits.length > 0 || (c && c.failed > 0)
        ? 'degraded'
        : c && c.checks > 0
          ? 'ok'
          : 'none';
    return { day, state };
  });
}

/** Outage figures for the tiles: count, total and longest downtime within the window. */
export function outageStats(incidents: Incident[], since: number, now: number) {
  const seconds = incidents
    .filter((i) => i.kind === 'downtime' && endOf(i, now) >= since)
    .map((i) => Math.max(0, endOf(i, now) - Math.max(since, Date.parse(i.startedAt))) / 1000);
  return {
    count: seconds.length,
    total: seconds.reduce((a, b) => a + b, 0),
    longest: seconds.reduce((a, b) => Math.max(a, b), 0),
  };
}

/**
 * S5 "Erreichbarkeit" (site-incidents.html): the state now, uptime per period, the response time,
 * the second location, a calendar of the last days and the history of outages.
 */
export function Incidents() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const [filter, setFilter] = useState<Filter>('all');
  const historyId = useId();
  const incidents = useQuery({
    queryKey: ['site', siteId, 'incidents', 'all'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/incidents', {
          params: { path: { siteId }, query: { status: 'all', limit: 200 } },
        }),
      ),
  });
  const uptime = useQuery({
    queryKey: ['site', siteId, 'uptime', 'calendar'],
    queryFn: () => fetchUptime(siteId),
  });

  const items = incidents.data?.items ?? [];
  const shown = items.filter((i) =>
    filter === 'open' ? !i.resolvedAt : filter === 'resolved' ? !!i.resolvedAt : true,
  );

  return (
    <>
      <AvailabilityHead siteId={siteId} />
      <StatusTiles siteId={siteId} />
      <PeriodStrip siteId={siteId} incidents={items} />
      <ResponseChart siteId={siteId} />
      <LocationsCard siteId={siteId} />
      {incidents.isPending ? <Loading /> : null}
      {incidents.isError ? (
        <LoadError
          message={errorMessage(incidents.error, t)}
          onRetry={() => void incidents.refetch()}
        />
      ) : null}
      {incidents.data ? (
        <>
          {uptime.data ? <Calendar items={items} uptime={uptime.data} /> : null}
          <section className="card" aria-labelledby={historyId}>
            <div className="hd">
              <h2 id={historyId}>{t('incidents.history')}</h2>
              <Segmented
                label={t('incidents.filter')}
                value={filter}
                options={(['all', 'open', 'resolved'] as const).map(
                  (f) => [f, t(`incidents.filters.${f}`)] as const,
                )}
                onChange={setFilter}
              />
            </div>
            {shown.length === 0 ? (
              <div className="empty">
                <b>{t('incidents.empty')}</b>
              </div>
            ) : (
              <ul className="hist">
                {shown.map((i) => (
                  <Entry key={i.id} incident={i} language={i18n.language} timeZone={timeZone} />
                ))}
              </ul>
            )}
            {shown.some((i) => i.confidence !== 'multi_region') ? (
              <p className="spec">{t('incidents.confidence')}</p>
            ) : null}
          </section>
        </>
      ) : null}
    </>
  );
}

const HEAT_CLASS: Record<DayState, string | undefined> = {
  ok: undefined,
  degraded: 'w',
  down: 'b',
  none: 'n',
};

/** The last 90 days – or the 7 every plan shows – one square each (components.css .heat). */
function Calendar({ items, uptime }: { items: Incident[]; uptime: Uptime }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const days = uptime.range === '7d' ? 7 : 90;
  const cells = calendarDays(days, uptime.series, items, Date.now());
  const date = (day: string) => formatDate(`${day}T12:00:00Z`, i18n.language, 'UTC');
  const count = (s: DayState) => cells.filter((c) => c.state === s).length;
  return (
    <section className="card" aria-labelledby={titleId}>
      <h2 id={titleId}>{t('incidents.calendar.title', { days })}</h2>
      {days < 90 ? <Notice kind="info">{t('incidents.calendar.limited')}</Notice> : null}
      <div
        role="img"
        aria-label={t('incidents.calendar.summary', {
          days,
          ok: count('ok'),
          degraded: count('degraded'),
          down: count('down'),
        })}
        className={days === 90 ? 'heat w13' : 'heat d7'}
      >
        {cells.map((c) => (
          <i
            key={c.day}
            data-state={c.state}
            className={HEAT_CLASS[c.state]}
            title={`${date(c.day)}: ${t(`incidents.calendar.${c.state}`)}`}
          />
        ))}
      </div>
      <div className="legend">
        {(['ok', 'degraded', 'down', 'none'] as const).map((s) => (
          <span key={s}>
            <i className={s} aria-hidden="true" />
            {t(`incidents.calendar.${s}`)}
          </span>
        ))}
        <span>
          {cells[0] ? date(cells[0].day) : null} – {t('incidents.calendar.today')}
        </span>
      </div>
    </section>
  );
}

/** One outage or disturbance of the history (site.css .hist). */
function Entry({
  incident: i,
  language,
  timeZone,
}: {
  incident: Incident;
  language: string;
  timeZone: string | undefined;
}) {
  const { t } = useTranslation();
  const seconds =
    i.durationSeconds ?? (i.resolvedAt ? null : (Date.now() - Date.parse(i.startedAt)) / 1000);
  return (
    <li>
      <span
        className={`ico${i.kind === 'downtime' || !i.resolvedAt ? '' : ' w'}`}
        aria-hidden="true"
      />
      <div>
        <b>
          {t(`incidents.kinds.${i.kind}`, { defaultValue: i.kind })} ·{' '}
          {t(`incidents.causes.${i.causeCode}`, {
            defaultValue: t('incidents.causes.fallback'),
          })}
        </b>
        {i.explanation?.what ? <span>{i.explanation.what}</span> : null}
        {i.confidence === 'multi_region' ? (
          <span className="badge acc nodot" title={t('incidents.multiRegionTitle')}>
            {t('incidents.multiRegion')}
          </span>
        ) : null}
      </div>
      <div className="meta">
        {formatDateTime(i.startedAt, language, timeZone)} –{' '}
        {i.resolvedAt ? formatDateTime(i.resolvedAt, language, timeZone) : '…'}
        <br />
        {seconds === null ? null : `${formatDuration(seconds, language)} · `}
        {i.resolvedAt ? t('incidents.resolved') : <strong>{t('incidents.ongoing')}</strong>}
        {i.flapping ? ` (${t('incidents.unstable')})` : null}
      </div>
    </li>
  );
}
